export type SwapProvider = 'changenow' | 'changelly'
export type SwapDirection = 'to-btc'

export interface SwapCurrency {
	ticker: string
	network: string
	name: string
	image: string
	featured: boolean
	hasExternalId: boolean
}

export type SwapAsset = Pick<SwapCurrency, 'ticker' | 'network'>

interface SwapPair {
	direction: SwapDirection
	fromCurrency: string
	fromNetwork: string
	toCurrency: 'btc'
	toNetwork: 'btc'
}

export interface SwapMinimum extends SwapPair {
	flow: 'standard'
	minAmount: number
	provider: SwapProvider
	providerName: string
}

export interface SwapRouteQuote extends SwapPair {
	flow: 'standard'
	type: 'direct'
	fromAmount: string
	toAmount: number
	provider: SwapProvider
	providerName: string
	transactionSpeedForecast?: string | null
	warningMessage?: string | null
	depositFee?: number | null
	withdrawalFee?: number | null
	grossToAmount?: number | null
}

export interface SwapQuote extends SwapRouteQuote {
	recommendedProvider: SwapProvider
	routes: SwapRouteQuote[]
	routeCount: number
}

export interface SwapExchange {
	id: string
	direction: SwapDirection
	provider: SwapProvider
	providerName: string
	status?: string
	fromCurrency: string
	/** Some provider status responses omit the chain; retain the created order's chain. */
	fromNetwork?: string
	toCurrency: 'btc'
	toNetwork: 'btc'
	fromAmount?: string | number
	toAmount?: number
	payinAddress: string
	payinExtraId?: string | null
	payoutAddress: string
	refundAddress?: string | null
	validUntil?: string | null
	expectedAmountFrom?: number | null
	/** The only amount suitable for deposit instructions or copying. */
	expectedAmountFromExact?: string | null
	expectedAmountTo?: number | null
	/** The selected route's estimate, saved locally; never a confirmed payout. */
	quotedAmountTo?: number
	amountFrom?: number | null
	/** Amount already received, not an instruction to send another deposit. */
	amountFromExact?: string | null
	amountTo?: number | null
	payinHash?: string | null
	payoutHash?: string | null
	createdAt?: string
	updatedAt?: string
}

export interface CreateSwapInput {
	asset: SwapAsset
	fromAmount: string
	address: string
	refundAddress: string
	refundExtraId?: string
	provider: SwapProvider
}

/** Normalize decimal text without sending it through floating-point arithmetic. */
export function normalizeSwapAmount(value: unknown): string | null {
	if (typeof value !== 'string') return null
	const raw = value.trim()
	if (raw.length > 128 || !/^(?:\d+(?:\.\d{1,18})?|\.\d{1,18})$/.test(raw)) return null
	const [integer, fraction = ''] = raw.split('.')
	const whole = integer.replace(/^0+/, '') || '0'
	const decimal = fraction.replace(/0+$/, '')
	if (whole === '0' && !decimal) return null
	return decimal ? `${whole}.${decimal}` : whole
}

function requireSwapAmount(value: string): string {
	const amount = normalizeSwapAmount(value)
	if (!amount) throw new Error('Enter an amount greater than zero with up to 18 decimal places.')
	return amount
}

function withParams(params: Record<string, string>): string {
	return `/api/swap?${new URLSearchParams(params).toString()}`
}

export async function requestSwapJson<T>(url: string, init?: RequestInit): Promise<T> {
	const headers = new Headers(init?.headers)
	headers.set('accept', 'application/json')
	const response = await fetch(url, { ...init, headers, cache: 'no-store' })
	const payload: unknown = await response.json().catch((error: unknown) => {
		if (init?.signal?.aborted || (isRecord(error) && error.name === 'AbortError')) throw error
		return null
	})
	if (!response.ok) {
		const message =
			isRecord(payload) && typeof payload.error === 'string'
				? payload.error
				: 'The swap provider could not complete this request. Please try again.'
		throw new Error(message)
	}
	if (!isRecord(payload)) throw new Error('The swap provider returned an invalid response.')
	return payload as T
}

export const swapRouter = {
	currencies: (signal?: AbortSignal) =>
		requestSwapJson<{ currencies: SwapCurrency[] }>(
			withParams({
				action: 'currencies',
				direction: 'to-btc'
			}),
			{ signal }
		),

	minimum: (asset: SwapAsset, signal?: AbortSignal) =>
		requestSwapJson<SwapMinimum>(
			withParams({
				action: 'minimum',
				direction: 'to-btc',
				currency: asset.ticker,
				network: asset.network
			}),
			{ signal }
		),

	quote: async (asset: SwapAsset, fromAmount: string, signal?: AbortSignal) =>
		requestSwapJson<SwapQuote>(
			withParams({
				action: 'quote',
				direction: 'to-btc',
				currency: asset.ticker,
				network: asset.network,
				fromAmount: requireSwapAmount(fromAmount)
			}),
			{ signal }
		),

	create: async (input: CreateSwapInput, signal?: AbortSignal) =>
		requestSwapJson<SwapExchange>(withParams({ action: 'create' }), {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			signal,
			body: JSON.stringify({
				direction: 'to-btc',
				currency: input.asset.ticker,
				network: input.asset.network,
				fromAmount: requireSwapAmount(input.fromAmount),
				address: input.address,
				refundAddress: input.refundAddress,
				refundExtraId: input.refundExtraId ?? '',
				provider: input.provider
			})
		}),

	status: (id: string, provider: SwapProvider, signal?: AbortSignal) =>
		requestSwapJson<SwapExchange>(withParams({ action: 'status', id, provider }), { signal })
}

const NETWORK_LABELS: Record<string, string> = {
	btc: 'Bitcoin',
	bitcoin: 'Bitcoin',
	eth: 'Ethereum',
	ethereum: 'Ethereum',
	erc20: 'Ethereum',
	bsc: 'BNB Smart Chain',
	bep20: 'BNB Smart Chain',
	binancesmartchain: 'BNB Smart Chain',
	bnb: 'BNB Beacon Chain',
	bep2: 'BNB Beacon Chain',
	trx: 'Tron',
	tron: 'Tron',
	trc20: 'Tron',
	sol: 'Solana',
	solana: 'Solana',
	matic: 'Polygon',
	polygon: 'Polygon',
	arb: 'Arbitrum',
	arbitrum: 'Arbitrum',
	arbitrumone: 'Arbitrum',
	op: 'Optimism',
	optimism: 'Optimism',
	avaxc: 'Avalanche C-Chain',
	avalanchecchain: 'Avalanche C-Chain',
	avax: 'Avalanche X-Chain',
	avaxp: 'Avalanche P-Chain',
	base: 'Base',
	ltc: 'Litecoin',
	litecoin: 'Litecoin',
	doge: 'Dogecoin',
	dogecoin: 'Dogecoin',
	xrp: 'XRP Ledger',
	ripple: 'XRP Ledger',
	xlm: 'Stellar',
	stellar: 'Stellar',
	ada: 'Cardano',
	cardano: 'Cardano',
	bch: 'Bitcoin Cash',
	bitcoincash: 'Bitcoin Cash',
	bsv: 'Bitcoin SV',
	bitcoinsv: 'Bitcoin SV',
	dot: 'Polkadot',
	atom: 'Cosmos',
	ton: 'TON',
	sui: 'Sui',
	near: 'NEAR',
	apt: 'Aptos',
	ftm: 'Fantom',
	zksync: 'zkSync Era',
	linea: 'Linea',
	osmo: 'Osmosis',
	inj: 'Injective',
	sei: 'Sei',
	hype: 'HyperEVM',
	hyperevm: 'HyperEVM',
	hypercore: 'Hyperliquid',
	robinhood: 'Robinhood Chain'
}

export function networkLabel(network: string | null | undefined): string {
	const code = network?.trim().toLowerCase() ?? ''
	return NETWORK_LABELS[code] ?? (code ? code.toUpperCase() : 'Unknown network')
}

export function currencyKey(asset: SwapAsset): string {
	return `${asset.ticker}:${asset.network}`
}

export function getDepositAmountExact(exchange: SwapExchange): string | null {
	return normalizeSwapAmount(exchange.expectedAmountFromExact)
}

export function getPayoutAmount(exchange: SwapExchange): number | null {
	// An estimate must never appear under "BTC sent". An explicit completed zero
	// is real data; a pending zero means the provider has not sent the payout yet.
	if (exchange.status?.toLowerCase() === 'finished') {
		return typeof exchange.amountTo === 'number' &&
			Number.isFinite(exchange.amountTo) &&
			exchange.amountTo >= 0
			? exchange.amountTo
			: null
	}
	return firstPositiveAmount(
		exchange.amountTo,
		exchange.expectedAmountTo,
		exchange.toAmount,
		exchange.quotedAmountTo
	)
}

function firstPositiveAmount(...values: unknown[]): number | null {
	return (
		values.find(
			(value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0
		) ?? null
	)
}

/** Carry the route the user actually chose through creation, polling, and reload. */
export function withSwapQuote(exchange: SwapExchange, quote: SwapRouteQuote): SwapExchange {
	if (
		exchange.provider !== quote.provider ||
		exchange.fromCurrency !== quote.fromCurrency ||
		(exchange.fromNetwork && exchange.fromNetwork !== quote.fromNetwork) ||
		exchange.toCurrency !== quote.toCurrency ||
		exchange.toNetwork !== quote.toNetwork
	)
		return exchange
	const estimate = firstPositiveAmount(quote.toAmount)
	return estimate === null ? exchange : { ...exchange, quotedAmountTo: estimate }
}

/** Display estimates only. Deposit amounts use getDepositAmountExact instead. */
export function formatSwapAmount(value: unknown): string {
	if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return '—'
	return new Intl.NumberFormat('en-US', { maximumFractionDigits: 8 }).format(value)
}

/** Partial status replies cannot erase deposit instructions or cross order/account boundaries. */
export function mergeExchangeUpdate(
	current: SwapExchange,
	update: Partial<SwapExchange>
): SwapExchange {
	for (const field of [
		'id',
		'provider',
		'payinAddress',
		'payoutAddress',
		'toCurrency',
		'toNetwork'
	] as const) {
		if (update[field] != null && update[field] !== '' && update[field] !== current[field])
			return current
	}
	const defined = Object.fromEntries(
		Object.entries(update).filter(([, value]) => value !== null && value !== undefined)
	)
	const merged: SwapExchange = { ...current, ...defined }
	// A status response can use a provider-specific ticker or omit its network.
	merged.fromCurrency = current.fromCurrency
	merged.fromNetwork = current.fromNetwork ?? update.fromNetwork
	merged.payinAddress = current.payinAddress
	merged.payoutAddress = current.payoutAddress
	merged.expectedAmountFromExact = getDepositAmountExact(merged) ?? getDepositAmountExact(current)
	// Keep estimates separate from amountTo, the actual payout. Provider status
	// responses can omit expectations or temporarily replace them with zero.
	merged.expectedAmountTo = firstPositiveAmount(update.expectedAmountTo, current.expectedAmountTo)
	merged.quotedAmountTo =
		firstPositiveAmount(current.quotedAmountTo, update.quotedAmountTo) ?? undefined
	if (merged.status?.toLowerCase() !== 'finished')
		merged.toAmount = firstPositiveAmount(update.toAmount, current.toAmount) ?? merged.toAmount
	if (
		merged.status?.toLowerCase() === 'finished' &&
		current.status?.toLowerCase() !== 'finished' &&
		update.amountTo == null
	)
		merged.amountTo = undefined
	return merged
}

export function isFinalSwap(exchange: SwapExchange): boolean {
	return ['finished', 'refunded', 'expired', 'overdue'].includes(
		exchange.status?.toLowerCase() ?? ''
	)
}

export function swapFundsMoved(exchange: SwapExchange): boolean {
	return (
		Boolean(exchange.payinHash?.trim()) ||
		['confirming', 'exchanging', 'sending', 'finished'].includes(
			exchange.status?.toLowerCase() ?? ''
		)
	)
}

export function isAwaitingSwapDeposit(exchange: SwapExchange): boolean {
	return (
		!swapFundsMoved(exchange) &&
		['new', 'waiting'].includes(exchange.status?.toLowerCase() ?? 'waiting')
	)
}

export function getSwapExpirationTime(exchange: SwapExchange): number | null {
	if (!exchange.validUntil) return null
	const timestamp = Date.parse(exchange.validUntil)
	return Number.isFinite(timestamp) ? timestamp : null
}

export function hasSwapDepositExpired(exchange: SwapExchange, now = Date.now()): boolean {
	const expiration = getSwapExpirationTime(exchange)
	return isAwaitingSwapDeposit(exchange) && expiration !== null && now >= expiration
}

type SwapStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
const STORAGE_PREFIX = 'ow.btcSwap.active.v1.'
const SAVED_FIELDS = [
	'id',
	'direction',
	'provider',
	'providerName',
	'status',
	'fromCurrency',
	'fromNetwork',
	'toCurrency',
	'toNetwork',
	'fromAmount',
	'toAmount',
	'payinAddress',
	'payinExtraId',
	'payoutAddress',
	'refundAddress',
	'validUntil',
	'expectedAmountFrom',
	'expectedAmountFromExact',
	'expectedAmountTo',
	'quotedAmountTo',
	'amountFrom',
	'amountFromExact',
	'amountTo',
	'payinHash',
	'payoutHash',
	'createdAt',
	'updatedAt'
] as const

function isRecord(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isAddress(value: unknown): value is string {
	return (
		typeof value === 'string' &&
		value.length > 0 &&
		value.length <= 160 &&
		!/[\s\u0000-\u001f]/.test(value)
	)
}

function isStoredExchange(value: unknown, payoutAddress: string): value is SwapExchange {
	if (!isRecord(value)) return false
	return (
		typeof value.id === 'string' &&
		/^[a-zA-Z0-9_-]{6,128}$/.test(value.id) &&
		value.direction === 'to-btc' &&
		(value.provider === 'changenow' || value.provider === 'changelly') &&
		typeof value.providerName === 'string' &&
		typeof value.fromCurrency === 'string' &&
		value.fromCurrency.length > 0 &&
		(value.fromNetwork === undefined || typeof value.fromNetwork === 'string') &&
		value.toCurrency === 'btc' &&
		value.toNetwork === 'btc' &&
		isAddress(value.payinAddress) &&
		value.payoutAddress === payoutAddress &&
		(value.status === undefined || typeof value.status === 'string') &&
		(value.payinHash == null || typeof value.payinHash === 'string') &&
		(value.payoutHash == null || typeof value.payoutHash === 'string') &&
		(value.validUntil == null || typeof value.validUntil === 'string')
	)
}

function savedExchange(exchange: SwapExchange): SwapExchange {
	// Persist provider order details only, even if a caller's object contains wallet data.
	return Object.fromEntries(
		SAVED_FIELDS.filter((field) => exchange[field] !== undefined).map((field) => [
			field,
			exchange[field]
		])
	) as unknown as SwapExchange
}

function storageKey(address: string): string {
	return `${STORAGE_PREFIX}${encodeURIComponent(address)}`
}

export function loadActiveSwap(address: string, storage?: SwapStorage): SwapExchange | null {
	if (!isAddress(address)) return null
	try {
		const value = (storage ?? window.localStorage).getItem(storageKey(address))
		if (!value) return null
		const exchange: unknown = JSON.parse(value)
		return isStoredExchange(exchange, address) ? savedExchange(exchange) : null
	} catch {
		return null
	}
}

export function saveActiveSwap(
	address: string,
	exchange: SwapExchange,
	storage?: SwapStorage
): boolean {
	if (!isAddress(address) || !isStoredExchange(exchange, address)) return false
	try {
		;(storage ?? window.localStorage).setItem(
			storageKey(address),
			JSON.stringify(savedExchange(exchange))
		)
		return true
	} catch {
		return false
	}
}

export function clearActiveSwap(address: string, storage?: SwapStorage): boolean {
	if (!isAddress(address)) return false
	try {
		;(storage ?? window.localStorage).removeItem(storageKey(address))
		return true
	} catch {
		return false
	}
}
